import { inject, Injectable } from '@angular/core';
import {
  selectAllEntities,
  selectEntity,
  setEntities,
} from '@ngneat/elf-entities';
import { GroupService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSubject, map, switchMap, take } from 'rxjs';
import { Arrangement, Group } from '@thiememeulenhoff/types';
import Cookies from 'js-cookie';
import * as stores from './group.store';

@Injectable({
  providedIn: 'root',
})
export class GroupFacade {
  readonly #groups = stores.groups;

  readonly #groupService = inject(GroupService);

  readonly groups$ = this.#groups.pipe(selectAllEntities());

  readonly #arrangement = new BehaviorSubject<Arrangement | null>(null);

  readonly #selectedGroupId = new BehaviorSubject<string>('');

  readonly selectedGroupId$ = this.#selectedGroupId.asObservable();

  readonly groupById$ = (id: string) => this.#groups.pipe(selectEntity(id));

  readonly selectedGroup$ = this.#selectedGroupId.pipe(
    switchMap((id) => this.groupById$(id)),
  );

  readonly validatedSelectedGroupId$ = this.selectedGroup$.pipe(
    map((group) => group?.id ?? ''),
  );

  setArrangement(arrangement: Arrangement) {
    if (this.#arrangement.value?.code === arrangement.code) {
      return;
    }

    this.fetchGroups(arrangement.methodObject.slug);

    this.#arrangement.next(arrangement);

    // TODO: https://tmjira.atlassian.net/browse/TLA-8511
    const cookieValue = Cookies.get(`selected_group_${arrangement.code}`);
    if (cookieValue) {
      this.#selectedGroupId.next(cookieValue);
    }
  }

  fetchGroups(scope: string, includeUserCount = false, allSchoolyears = false) {
    return this.#groupService
      .fetchGroups(scope, includeUserCount, allSchoolyears)
      .pipe(take(1))
      .subscribe((groupsIndex) => this.setGroups(groupsIndex.items));
  }

  selectGroup(id: string) {
    if (this.#arrangement.value === null) {
      return;
    }

    this.#selectedGroupId.next(id);
    Cookies.set(`selected_group_${this.#arrangement.value.code}`, id);
  }

  // #region Store mutations
  setGroups = (groups: Array<Group>) => {
    this.#groups.update(setEntities(groups));
  };
  // #endregion
}
